import Decimal from 'decimal.js';
import { useIntl } from 'react-intl';

import { Currency } from '../../types/currency';
import { ISSUE_LTV } from '../utils/calculatorConfig';
import { ABBREVIATE_THRESHOLD, abbreviateNumber } from '../utils/abbreviate';
import { InfoTooltip } from './InfoTooltip';
import styles from './LoanDetailsPanel.module.less';

interface Props {
  currency: Currency;
  interestRate: Decimal;
  liquidationPriceValue: Decimal;
  maturityRepaymentValue: Decimal;
  originationFeeRate: string;
  isMobile: boolean;
}

// On mobile, fiat values ≥ 100,000 collapse to k/M to prevent overflow per spec.
function formatFiat(value: Decimal, locale: string, isMobile: boolean): string {
  if (isMobile && value.abs().gte(ABBREVIATE_THRESHOLD)) {
    return abbreviateNumber(value, locale);
  }
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(Number(value.toFixed(0)));
}

function formatPercent(value: Decimal, locale: string): string {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(Number(value.toFixed(2)));
}

export function LoanDetailsPanel({
  currency,
  interestRate,
  liquidationPriceValue,
  maturityRepaymentValue,
  originationFeeRate,
  isMobile,
}: Props) {
  const intl = useIntl();
  const locale = intl.locale;

  const rateText = `${formatPercent(interestRate, locale)}% p.a.`;
  const ltvText = `${ISSUE_LTV.mul(100).toFixed(0)}%`;
  const liquidationText = intl.formatMessage(
    { id: 'calculator.liquidationAt', defaultMessage: 'Liquidation at {price}' },
    { price: `${formatFiat(liquidationPriceValue, locale, isMobile)} ${currency}` }
  );
  const repaymentText = formatFiat(maturityRepaymentValue, locale, isMobile);
  const feeText = intl.formatMessage(
    { id: 'calculator.originationFeeFootnote', defaultMessage: '+ Origination fee {rate}% p.a.' },
    { rate: originationFeeRate }
  );

  return (
    <aside className={styles.panel} aria-label="Loan details">
      <div className={styles.title}>
        {intl.formatMessage({ id: 'calculator.loanDetails', defaultMessage: 'Loan details' })}
      </div>

      <div className={styles.stat}>
        <div className={styles.statHead}>
          <span className={styles.statLabel}>
            {intl.formatMessage({ id: 'calculator.interestRate', defaultMessage: 'Interest rate' })}
          </span>
          <InfoTooltip
            ariaLabel="About this interest rate"
            content={intl.formatMessage({
              id: 'calculator.interestRateTooltip',
              defaultMessage:
                'Past 7-day marketplace average for the selected period and currency. Indicative — final rate is set when your loan is matched.',
            })}
          />
        </div>
        <div className={styles.statValue}>
          <span className={styles.greyed}>~</span>
          <span>{' '}</span>
          <span>{rateText}</span>
        </div>
      </div>

      <div className={styles.stat}>
        <div className={styles.statHead}>
          <span className={styles.statLabel}>
            {intl.formatMessage({ id: 'calculator.ltv', defaultMessage: 'LTV' })}
          </span>
        </div>
        <div className={styles.statValue}>{ltvText}</div>
        <div className={styles.statFootnote}>{liquidationText}</div>
      </div>

      <div className={styles.stat}>
        <div className={styles.statHead}>
          <span className={styles.statLabel}>
            {intl.formatMessage({
              id: 'calculator.maturityRepayment',
              defaultMessage: 'Maturity repayment',
            })}
          </span>
        </div>
        <div className={styles.statValue}>
          <span>{repaymentText}</span>
          <span>{' '}</span>
          <span className={styles.greyed}>{currency}</span>
        </div>
        <div className={styles.statFootnote}>{feeText}</div>
      </div>
    </aside>
  );
}
